import React from 'react'

import {FormikProps} from 'formik'
import compact from 'lodash/compact'
import every from 'lodash/every'
import mapValues from 'lodash/mapValues'
import * as Yup from 'yup'

import {useAppDispatch} from '../../hooks/redux'
import {openCreateProductionTimeModal} from '../../redux/features/modal'

import {formatTimeRange} from '../../libs/formatter'
import {
  PossiblyMixedValue,
  isMixedValue,
  toMixedValidationSchema,
  toNumberValue,
  unlockMixedValuesLabel,
} from '../utils/form'

import {
  Option,
  OptionId,
  OptionWithLinkedProviderInfos,
  ProductionTimeOption,
  ProviderInfo,
} from '../../types'

import FieldNote from '../../../../commons/src/components/field-note'
import FormRow from '../../../../commons/src/components/form-row'
import Headline from '../../../../commons/src/components/headline'
import Link from '../../../../commons/src/components/link'
import NumberInputField from '../../../../commons/src/components/number-input-field'
import Paragraph from '../../../../commons/src/components/paragraph'
import Stack from '../../../../commons/src/components/stack'
import MixedValueFormSection from '../../components/mixed-value-form-section'

import MultiSelectFieldPartial from './multi-select-field-partial'

export type SingleFormData = {
  productionTimeFast: string
  productionTimeSlow: string
  productionTimeIds: OptionId[]
  formikGeneral?: unknown // Need type to be able to set general validation error.
}

export type MixedFormData = {
  productionTimeFast: PossiblyMixedValue<string>
  productionTimeSlow: PossiblyMixedValue<string>
  productionTimeIds: PossiblyMixedValue<OptionId[]>
  formikGeneral?: unknown // Need type to be able to set general validation error.
}

export const initialValues = {
  productionTimeFast: '',
  productionTimeSlow: '',
  productionTimeIds: [],
}

export const createValidationSchemaWithProductionTimes = (
  productionTimes: OptionWithLinkedProviderInfos[]
) =>
  Yup.lazy(values => {
    const linkedProductionTimes = productionTimes.filter(
      ({id}) => Array.isArray(values.productionTimeIds) && values.productionTimeIds.includes(id)
    )

    const expeditedProductionTimes = linkedProductionTimes.filter(
      option => option.type === ProductionTimeOption.EXPEDITED
    )
    const economyProductionTimes = linkedProductionTimes.filter(
      option => option.type === ProductionTimeOption.ECONOMY
    )

    return toMixedValidationSchema({
      productionTimeFast: Yup.number()
        .label('Production time minimum')
        .integer()
        // Default Fast > Expedited Slow
        .min(
          Math.max(
            3,
            ...compact(
              expeditedProductionTimes.map(productionTime =>
                productionTime.productionTimeSlowTotal
                  ? productionTime.productionTimeSlowTotal + 1
                  : null
              )
            )
          )
        )
        // Default Fast < Economy Fast
        .max(
          Math.min(
            15,
            ...compact(
              economyProductionTimes.map(productionTime =>
                productionTime.productionTimeFastTotal
                  ? productionTime.productionTimeFastTotal - 1
                  : null
              )
            )
          )
        )
        .required(),
      productionTimeSlow: Yup.number()
        .label('Production time maximum')
        .integer()
        .min(Yup.ref('productionTimeFast'))
        // Default Slow > Expedited Slow
        .min(
          Math.max(
            values.productionTimeFast,
            ...compact(
              expeditedProductionTimes.map(productionTime =>
                productionTime.productionTimeSlowTotal
                  ? productionTime.productionTimeSlowTotal + 1
                  : null
              )
            )
          )
        )
        // Default Slow < Economy Slow
        .max(
          Math.min(
            15,
            ...compact(
              economyProductionTimes.map(productionTime =>
                productionTime.productionTimeSlowTotal
                  ? productionTime.productionTimeSlowTotal - 1
                  : null
              )
            )
          )
        )
        .test(
          'interval',
          'Production time interval should be less than or equal to 10 business days',
          (value, ctx) => {
            if (
              value &&
              ctx.parent.productionTimeFast &&
              value > ctx.parent.productionTimeFast + 10
            ) {
              return ctx.createError({path: ctx.path})
            }
            return true
          }
        )
        .required(),
    })
  })

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const formDataTransforms: Record<string, (value: any) => any> = {
  productionTimeFast: toNumberValue,
  productionTimeSlow: toNumberValue,
}

export const formDataToPartialRequest = (formData: Partial<SingleFormData>) =>
  mapValues(formData, (value, key) => {
    const providerInfoRequestTransform = formDataTransforms[key]
    return providerInfoRequestTransform ? providerInfoRequestTransform(value) : undefined
  })

export const formatProductionTimeName = (
  productionTime: OptionWithLinkedProviderInfos,
  invalid = false
) => {
  const name = productionTime.name
  const type = productionTime.type
  const days = formatTimeRange(
    productionTime.productionTimeFastTotal || undefined,
    productionTime.productionTimeSlowTotal || undefined
  )

  const valid = invalid ? ' - N/A' : ''

  return name ? `${name} (${type}, ${days})${valid}` : `${type}, ${days}${valid}`
}

export const sortProductionTimes = (productionTimes: OptionWithLinkedProviderInfos[]) =>
  productionTimes.sort((a, b) =>
    formatProductionTimeName(a).localeCompare(formatProductionTimeName(b), undefined, {
      numeric: true,
    })
  )

export const validateProductionTimeOption = (
  productionTime: OptionWithLinkedProviderInfos,
  providerInfos: MixedFormData
) => {
  let isValid = true
  const providerInfoProductionTimesFast = (
    isMixedValue(providerInfos.productionTimeFast)
      ? providerInfos.productionTimeFast.values
      : [providerInfos.productionTimeFast]
  ).map(Number)

  const providerInfoProductionTimesSlow = (
    isMixedValue(providerInfos.productionTimeSlow)
      ? providerInfos.productionTimeSlow.values
      : [providerInfos.productionTimeSlow]
  ).map(Number)

  if (productionTime.type === ProductionTimeOption.ECONOMY) {
    isValid = Boolean(
      productionTime.productionTimeFastTotal &&
        // Economy Fast > Default Fast
        productionTime.productionTimeFastTotal > Math.max(...providerInfoProductionTimesFast) &&
        productionTime.productionTimeSlowTotal &&
        // Economy Slow > Default Slow
        productionTime.productionTimeSlowTotal > Math.max(...providerInfoProductionTimesSlow)
    )
  }

  if (productionTime.type === ProductionTimeOption.EXPEDITED) {
    isValid = Boolean(
      productionTime.productionTimeFastTotal &&
        // Expedited Fast < Default Fast
        productionTime.productionTimeFastTotal < Math.min(...providerInfoProductionTimesFast) &&
        productionTime.productionTimeSlowTotal &&
        // Expedited Slow < Default Fast
        productionTime.productionTimeSlowTotal < Math.min(...providerInfoProductionTimesFast)
    )
  }

  return isValid
}

type Props = {
  formik: FormikProps<SingleFormData> | FormikProps<MixedFormData>
  onRefresh: () => void
  submitButton?: React.ReactNode
  isLoading: boolean
  options: {label: string; value: string}[]
  reopenPreviousModal?: (productionTimeId: string) => void
  providerInfos: ProviderInfo[]
}

const ProductionTimesFormPartial = ({
  formik,
  onRefresh,
  submitButton,
  isLoading,
  options,
  reopenPreviousModal,
  providerInfos,
}: Props) => {
  const dispatch = useAppDispatch()

  const defaultProductionTimes = {
    productionTimeFast: Number(formik.values.productionTimeFast),
    productionTimeSlow: Number(formik.values.productionTimeSlow),
  }

  const hasDefaultProductionTimes = every(Object.values(defaultProductionTimes), Boolean)

  const setCreatedProductionTime = (productionTime: Option) => {
    if (reopenPreviousModal) {
      reopenPreviousModal(productionTime.id)
    } else {
      formik.setFieldValue('productionTimeIds', [
        ...(formik.values.productionTimeIds as OptionId[]),
        productionTime.id,
      ])

      onRefresh()
    }
  }

  const clearProductionTimes = () => {
    formik.setValues({
      ...formik.values,
      productionTimeFast: '',
      productionTimeSlow: '',
    } as SingleFormData)
  }

  return (
    <form onSubmit={formik.handleSubmit}>
      {formik.errors.formikGeneral && <Paragraph error>{formik.errors.formikGeneral}</Paragraph>}
      <Headline size="s" label="Default production time" className="u-margin-bottom" />
      <Stack space="300">
        <MixedValueFormSection
          locked={
            isMixedValue(formik.values.productionTimeFast) ||
            isMixedValue(formik.values.productionTimeSlow)
          }
          onClick={() => clearProductionTimes()}
          unlockButtonLabel={unlockMixedValuesLabel}
        >
          <FormRow layout="half-half" divider="-">
            <FieldNote
              appearance="error"
              note={formik.submitCount > 0 && formik.errors.productionTimeFast}
            >
              <NumberInputField
                placeholder="min"
                name="productionTimeFast"
                mixedLabel="Mixed"
                mixed={isMixedValue(formik.values.productionTimeFast)}
                value={
                  isMixedValue(formik.values.productionTimeFast)
                    ? initialValues.productionTimeFast
                    : formik.values.productionTimeFast
                }
                suffix="business day(s)"
                appearance={
                  formik.submitCount > 0 && !!formik.errors.productionTimeFast ? 'error' : undefined
                }
                onChange={(value, name) => formik.setFieldValue(name as string, value)}
                onBlur={formik.handleBlur}
              />
            </FieldNote>
            <FieldNote
              appearance="error"
              note={formik.submitCount > 0 && formik.errors.productionTimeSlow}
            >
              <NumberInputField
                placeholder="max"
                name="productionTimeSlow"
                mixedLabel="Mixed"
                mixed={isMixedValue(formik.values.productionTimeSlow)}
                value={
                  isMixedValue(formik.values.productionTimeSlow)
                    ? initialValues.productionTimeSlow
                    : formik.values.productionTimeSlow
                }
                suffix="business day(s)"
                appearance={
                  formik.submitCount > 0 && !!formik.errors.productionTimeSlow ? 'error' : undefined
                }
                onChange={(value, name) => formik.setFieldValue(name as string, value)}
                onBlur={formik.handleBlur}
              />
            </FieldNote>
          </FormRow>
        </MixedValueFormSection>
        <div>
          <Headline size="s" label="Production time options" className="u-margin-bottom" />
          {!isLoading && !isMixedValue(formik.values.productionTimeIds) && (
            <Paragraph>
              You can either select one or more of your existing production time options or you can{' '}
              <Link
                onClick={() =>
                  dispatch(
                    openCreateProductionTimeModal({
                      linkedConfigsCount: providerInfos.length,
                      defaultProductionTimes,
                      onDone: setCreatedProductionTime,
                    })
                  )
                }
              >
                create a new one
              </Link>
              .
            </Paragraph>
          )}

          <FormRow className="u-no-margin-bottom">
            <MultiSelectFieldPartial
              isLoading={isLoading}
              selectLabel="Link production time option"
              options={options}
              disabled={!hasDefaultProductionTimes}
              values={formik.values.productionTimeIds}
              onChange={(values: OptionId[]) => {
                formik.setFieldValue('productionTimeIds', values)
              }}
              onReset={() =>
                formik.setFieldValue('productionTimeIds', initialValues.productionTimeIds)
              }
            />
            {!isMixedValue(formik.values.productionTimeFast) &&
              !isMixedValue(formik.values.productionTimeSlow) && (
                <Paragraph size="s">
                  <i>
                    {hasDefaultProductionTimes ? (
                      <>
                        Linked production times can be unavailable (N/A) because they overlap with
                        default production times. Please note that the following requirements must
                        be given:
                        <br />
                        - Economy: min &gt; Default min & max &gt; Default max
                        <br />- Expedited: min &lt; Default min & max &lt; Default min
                      </>
                    ) : (
                      'Please set your default production times before linking more options.'
                    )}
                  </i>
                </Paragraph>
              )}
          </FormRow>
        </div>
        {submitButton}
      </Stack>
    </form>
  )
}

export default ProductionTimesFormPartial
